import React, { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router';
import { logger } from '../utils/logger';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requireAdmin = false }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [isCheckingAccess, setIsCheckingAccess] = useState(requireAdmin);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (!requireAdmin) {
        setHasAccess(true);
        setIsCheckingAccess(false);
        return;
      }

      if (!user) {
        setHasAccess(false);
        setIsCheckingAccess(false);
        return;
      }

      try {
        logger.info('[ProtectedRoute] Checking access for admin route...');
        const session = await fetchAuthSession();
        const groups = session.tokens?.idToken?.payload?.['cognito:groups'];
        logger.info('[ProtectedRoute] Groups from token', { groups });

        if (groups) {
          const groupArray = Array.isArray(groups) ? groups : [groups];
          const allowed = groupArray.some((group: any) =>
            group === 'admins' ||
            group === 'tempest' ||
            group === 'tempest-hosts' ||
            group === 'guest-hosts'
          );
          logger.info(`[ProtectedRoute] Access granted: ${allowed}`);
          setHasAccess(allowed);
        } else {
          logger.info('[ProtectedRoute] No groups found, access denied');
          setHasAccess(false);
        }
      } catch (error) {
        logger.error('[ProtectedRoute] Error checking access', error);
        setHasAccess(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, [user, requireAdmin]);

  if (!user) {
    logger.info('[ProtectedRoute] No user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (isCheckingAccess) {
    logger.info('[ProtectedRoute] Checking access...');
    return <div className="container"><div className="loading"><div className="spinner"></div></div></div>;
  }

  if (requireAdmin && !hasAccess) {
    logger.info('[ProtectedRoute] Access denied, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  logger.info('[ProtectedRoute] Access granted, rendering route');
  return <Outlet />;
};

export default ProtectedRoute;
